import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Container,
} from '@mui/material';
import {
  TrendingUp,
  LocalPharmacy,
  Inventory,
  Warning,
  PointOfSale,
  Notifications,
} from '@mui/icons-material';
import { analyticsAPI, inventoryAPI } from '../../services/API';
import { useAuth } from '../../contexts/AuthContext';

const StatCard = ({ title, value, subtitle, icon, color = 'primary' }) => (
  <Card sx={{ height: '100%', borderRadius: 3, boxShadow: 3 }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography color="text.secondary" gutterBottom variant="body1">
            {title}
          </Typography>
          <Typography variant="h3" component="div" fontWeight="bold">
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="body1" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{ color: `${color}.main` }}>{icon}</Box>
      </Box>
    </CardContent>
  </Card>
);

export const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [analyticsResponse, alertsResponse] = await Promise.all([
        analyticsAPI.getDashboard(),
        inventoryAPI.getExpiryAlerts(),
      ]);

      setDashboardData({
        ...analyticsResponse.data.data,
        expiryAlerts: alertsResponse.data.data.alerts,
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LinearProgress />;
  }

  if (!dashboardData) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Error loading dashboard data</Typography>
      </Box>
    );
  }

  const { overview, alerts, recentSales, expiryAlerts } = dashboardData;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)',
      }}
    >
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" gutterBottom fontWeight="bold">
            Welcome back, {user?.name}!
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Here's what's happening in your pharmacy today.
          </Typography>
        </Box>

        {/* Statistics Grid */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Today's Revenue"
              value={`₹${overview.todayRevenue.toLocaleString()}`}
              subtitle={`${overview.todayTransactions} transactions`}
              icon={<TrendingUp sx={{ fontSize: 40 }} />}
              color="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Monthly Revenue"
              value={`₹${overview.monthlyRevenue.toLocaleString()}`}
              icon={<PointOfSale sx={{ fontSize: 40 }} />}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Low Stock"
              value={overview.lowStockCount}
              subtitle="Items need attention"
              icon={<Inventory sx={{ fontSize: 40 }} />}
              color="warning"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Expiring Soon"
              value={overview.expiryAlertCount}
              subtitle="Check inventory"
              icon={<Warning sx={{ fontSize: 40 }} />}
              color="error"
            />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          {/* Recent Sales */}
          <Grid item xs={12} md={8}>
            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent>
                <Typography variant="h5" gutterBottom fontWeight="bold">
                  Recent Sales
                </Typography>
                <List>
                  {recentSales?.map((sale, index) => (
                    <ListItem key={sale._id} divider={index < recentSales.length - 1}>
                      <ListItemIcon>
                        <PointOfSale color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`Invoice: ${sale.invoiceNumber}`}
                        secondary={`₹${sale.grandTotal} • ${new Date(
                          sale.saleDate
                        ).toLocaleDateString()}`}
                      />
                      <Chip
                        label={sale.paymentStatus}
                        size="small"
                        color={sale.paymentStatus === 'paid' ? 'success' : 'warning'}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Alerts */}
          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent>
                <Typography variant="h5" gutterBottom fontWeight="bold">
                  Important Alerts
                </Typography>
                <List dense>
                  {alerts.lowStock.slice(0, 3).map((item, index) => (
                    <ListItem key={index} divider={index < 2}>
                      <ListItemIcon>
                        <Warning color="warning" />
                      </ListItemIcon>
                      <ListItemText
                        primary={item.medicine.name}
                        secondary={`Only ${item.availableQuantity} left`}
                      />
                    </ListItem>
                  ))}
                  {expiryAlerts.slice(0, 2).map((alert, index) => (
                    <ListItem key={alert._id} divider>
                      <ListItemIcon>
                        <Notifications color="error" />
                      </ListItemIcon>
                      <ListItemText
                        primary={alert.medicine.name}
                        secondary={`Expires in ${alert.daysUntilExpiry} days`}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};